import { ComponentChild, RefObject } from 'preact';
import { useState } from 'preact/hooks';
import { useTooltipControlled } from '#hooks/UNSAFE_useTooltip';

type Props = {
  fieldRef?: RefObject<HTMLElement>;
  hasTruncatedLabel?: boolean;
  isDisabled: boolean;
  label?: string;
};

/**
 * A hook that calls useTooltipControlled and uses a logical focus strategy to control the tooltip.
 * When hasTruncatedLabel is true, we call onLogicalFocus to make the tooltip appear; otherwise we
 * call onLogicalBlur to dismiss it. See useTextField.ts for how we set the value of hasTruncatedLabel.
 */
export const useTextFieldTooltip = ({
  fieldRef,
  hasTruncatedLabel,
  isDisabled,
  label
}: Props): {
  tooltipContent: ComponentChild;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tooltipEventHandlerProps: Record<string, any>;
} => {
  const [isOpen, setOpen] = useState(false);
  const { tooltipProps, tooltipContent } = useTooltipControlled({
    dismissOnClick: false,
    displayOnHover: false,
    focusStrategy: 'logical',
    isDisabled,
    isOpen: isOpen,
    offset: { mainAxis: 4 },
    onToggle: ({ value }) => {
      setOpen(value);
    },
    position: 'top-start',
    text: label
  });

  // The label already has a for-id association with the input
  // and is read by AT, so we don't need aria-describedby.
  const {
    'aria-describedby': notNeeded,
    onLogicalBlur,
    onLogicalFocus,
    ...tooltipEventHandlerProps
  } = tooltipProps;

  if (!isDisabled) {
    if (hasTruncatedLabel) {
      onLogicalFocus?.(fieldRef?.current);
    } else {
      onLogicalBlur?.();
    }
  }

  return { tooltipContent, tooltipEventHandlerProps };
};
